import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { getSite } from "@/data/site";
import { useLang, LangSwitch } from "@/i18n/lang";
import { EASE } from "@/lib/ui";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else if (el) el.scrollIntoView({ behavior: "smooth" });
};

const Logo = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId("home"), 400);
    } else {
      scrollToId("home");
    }
  };

  return (
    <button
      onClick={handleClick}
      data-testid="nav-logo"
      className="flex items-center select-none"
    >
      <img
        src="/logo-full.png"
        alt="Glamour Dent"
        className="h-11 md:h-12 w-auto"
      />
    </button>
  );
};

export default function Navbar() {
  const { lang } = useLang();
  const { NAV, CONTACT, book } = getSite(lang);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => scrollToId(id), 500);
      } else scrollToId(id);
    } else navigate(to);
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: EASE, delay: 0.2 }}
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "py-2 glass-dark" : "py-4 bg-transparent"}`}
        data-testid="navbar"
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <Logo />
          <ul className="hidden lg:flex items-center gap-8">
            {NAV.map((item) => (
              <li key={item.label}>
                <button
                  data-testid={`nav-link-${item.to}`}
                  onClick={() => goTo(item.to)}
                  className="relative font-accent text-[13px] tracking-[0.14em] uppercase text-white/70 hover:text-white transition-colors duration-300 group"
                >
                  {item.label}
                  <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold group-hover:w-full transition-all duration-400 ease-out" />
                </button>
              </li>
            ))}
          </ul>
          <div className="hidden lg:flex items-center gap-4">
            <LangSwitch />
            <a
              href={CONTACT.phoneHref}
              data-testid="nav-book-btn"
              className="group relative inline-flex items-center gap-2 rounded-full bg-gold px-6 py-2.5 font-accent text-[13px] tracking-wide text-ink font-medium overflow-hidden"
            >
              <Phone className="w-3.5 h-3.5 relative z-10" />
              <span className="relative z-10">{book}</span>
              <span className="absolute inset-0 bg-gold-light translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out" />
            </a>
          </div>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
            onClick={() => setOpen(true)}
            className="lg:hidden text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <Logo />
              <button
                data-testid="mobile-menu-close"
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="text-white"
              >
                <X className="w-7 h-7" />
              </button>
            </div>
            <ul className="flex-1 flex flex-col justify-center gap-2 px-8">
              {NAV.map((item, i) => (
                <motion.li
                  key={item.label}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, ease: EASE }}
                >
                  <button
                    onClick={() => goTo(item.to)}
                    className="font-serif text-4xl text-white/90 hover:text-gold transition-colors"
                  >
                    {item.label}
                  </button>
                </motion.li>
              ))}
            </ul>
            <div className="px-8 pb-12 flex items-center justify-between">
              <a
                href={CONTACT.phoneHref}
                className="flex items-center gap-3 text-gold font-accent tracking-wide"
              >
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
              <LangSwitch />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
